import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useAllMerchandisers from '../../redux/composableDevice/hook/useAllMerchandisers';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import MerchandiserContainer from '../Merchandiser/MerchandiserContainer';
import { getPolyglot } from '../../../../i18n';
import { useParams } from 'react-router-dom';
import AddMerchandiserModal from '../Merchandiser/AddMerchandiserModal';
import { AddMerchandiserIcon } from '../../theme/icons';
import { StoreSubheader } from './StoreSubheader';
import {
  getGroupByIdWithFallbackSelector,
  getGroupsByWorkspaceLoadingSelector,
} from '../../../../redux/groups/selectors';
import { useAppSelector } from '../../../../redux/store.model';
import { getMyWorkspaceSelector } from '../../../../redux/stoerkID/selectors/StoerkId.selectors';

const MerchandisersPage = () => {
  const params = useParams<{ merchandiserId: string }>();
  const groupId = params.merchandiserId;
  const myWorkspaceId = useAppSelector(getMyWorkspaceSelector)?.id ?? '';

  const { merchandisers, isLoading: isLoadingMerchandisers } =
    useAllMerchandisers({
      groupId,
      workspaceId: myWorkspaceId,
    });
  const selectedGroup = useAppSelector((state) =>
    getGroupByIdWithFallbackSelector(state, {
      groupId: groupId ?? '',
      workspaceId: myWorkspaceId ?? '',
    })
  );
  const isLoadingGroupsByWorkspace = useAppSelector((state) =>
    getGroupsByWorkspaceLoadingSelector(state, { workspaceId: myWorkspaceId })
  );
  const polyglot = getPolyglot();

  const sortedMerchandisers = useMemo(
    () => [...merchandisers].sort((a, b) => a.name.localeCompare(b.name)),
    [merchandisers]
  );

  const [openAddMerchanidserModal, setOpenAddMerchanidserModal] =
    useState(false);

  const [collapsedId, setCollapsedId] = useState<string[]>([]);
  const [firstRun, setFirstRun] = useState<boolean>(false);

  useEffect(() => {
    if (
      sortedMerchandisers.length === 1 &&
      !collapsedId?.includes(sortedMerchandisers[0].id) &&
      !firstRun
    ) {
      const merchandiser = sortedMerchandisers[0];
      const canBeCollapsed = Boolean(merchandiser?.channels?.length);
      if (canBeCollapsed) {
        setCollapsedId([merchandiser.id]);
        setFirstRun(true);
      }
    }
  }, [sortedMerchandisers, collapsedId, firstRun]);

  const isLoading = isLoadingMerchandisers || isLoadingGroupsByWorkspace;

  const handleCollapse = useCallback((merchandiserId: string) => {
    setCollapsedId((prev: string[]) => {
      if (prev?.includes(merchandiserId)) {
        return prev.filter((id) => id !== merchandiserId);
      } else {
        return prev ? [...prev, merchandiserId] : [merchandiserId];
      }
    });
  }, []);

  const addButton = (
    <Container
      maxWidth="xl"
      sx={{ paddingTop: '3rem', backgroundColor: 'background.container' }}
    >
      <Button
        onClick={() => setOpenAddMerchanidserModal(true)}
        style={{ borderRadius: '1rem', marginBottom: '2rem' }}
        fullWidth
        variant="outlined"
        sx={{
          '&:hover': {
            backgroundColor: 'primary.light',
            color: 'primary.contrastText',
            border: 'none',
          },
        }}
        startIcon={<AddMerchandiserIcon />}
      >
        {polyglot.t('freshwatch.add_merchandiser')}
      </Button>
    </Container>
  );
  const backButtonView = <StoreSubheader group={selectedGroup} />;

  let content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem',
        backgroundColor: 'background.default',
      }}
    >
      {sortedMerchandisers.map((merchandiser) => (
        <MerchandiserContainer
          key={merchandiser.id}
          merchandiser={merchandiser}
          collapsed={!collapsedId.includes(merchandiser.id)}
          onCollapse={handleCollapse}
        />
      ))}
    </Box>
  );

  if (!sortedMerchandisers || sortedMerchandisers.length === 0) {
    content = (
      <Box
        display={'flex'}
        flexDirection="column"
        alignItems="center"
        padding={3}
      >
        <Typography variant="h2">{polyglot.t('freshwatch.welcome')}</Typography>
        <Typography variant="h5" padding={2} color={'text.primary'}>
          {polyglot.t('freshwatch.add_merchandiser_description')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        maxWidth: 'xl',
        margin: '0 auto',
        backgroundColor: 'background.container',
      }}
    >
      {isLoading ? (
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress />
        </Container>
      ) : (
        <>
          {backButtonView}
          {content}
          {addButton}
        </>
      )}
      {/*
        Kept outside the loading branch so the modal stays mounted while the
        groups refetch (triggered by creating a merchandiser) is in flight.
        Otherwise the page swaps to the spinner and the modal flickers
        (closes, reopens, then closes) during creation.
      */}
      <AddMerchandiserModal
        groupId={groupId ?? ''}
        open={openAddMerchanidserModal}
        onClose={() => setOpenAddMerchanidserModal(false)}
      />
    </Box>
  );
};
export default MerchandisersPage;
